import type { Fetcher } from '@remix-run/react';
import * as htmlToImage from 'html-to-image';
import { dimensions } from '~/components/DesignLab/Canvas';
import type { ImprintAreaWithImage } from '~/routes/design-lab/bulk';
import type { AttributeValue, ErrorResponse } from '~/services/api-utils.server';
import type {
  AttributeValues,
  Cart,
  CartItem,
  CartItemExtraFields,
  CartItemPack,
  CartItemPackPart,
  CartItemProduct,
  CartItemProductPart,
  CustomPack,
  CustomPackOrProduct,
  CustomPackTemplate,
  DesignStatus,
  ItemAttributes,
  PackProduct,
  PackTemplate,
  PartLine,
  PaymentMethodsResponse,
  Product,
} from '~/services/api.server';
import { baseImgUrl } from '~/utils/constants';
import { CartItemType, DesignStatusId, ProductTypes } from './constants';
import {
  AUTOMOCKUPS_PARAM,
  DOMAIN_PARAM,
  LOGO_PARAM,
  SEARCH_PARAM,
  TAGS_PARAM,
  getPageParamValue,
  getSearchParamValue,
} from './searchParams';

export function shuffle(array: Array<Product>) {
  let currentIndex = array.length,
    randomIndex;
  while (currentIndex != 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;
    [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
  }
  return array;
}

export function newShade(hexColor: string, magnitude: number) {
  hexColor = hexColor.replace(`#`, ``);
  if (hexColor.length === 6) {
    const decimalColor = parseInt(hexColor, 16);
    let r = (decimalColor >> 16) + magnitude;
    r > 255 && (r = 255);
    r < 0 && (r = 0);
    let g = (decimalColor & 0x0000ff) + magnitude;
    g > 255 && (g = 255);
    g < 0 && (g = 0);
    let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
    b > 255 && (b = 255);
    b < 0 && (b = 0);
    return `#${(g | (b << 8) | (r << 16)).toString(16)}`;
  } else {
    return hexColor;
  }
}

export function deepEqual(object1: object | undefined, object2: object | undefined) {
  if (!object1 || !object2) {
    return false;
  }
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);
  if (keys1.length !== keys2.length) {
    return false;
  }
  for (const key of keys1) {
    const val1 = object1[key as keyof typeof object1];
    const val2 = object2[key as keyof typeof object1];
    const areObjects = isObject(val1) && isObject(val2);
    if ((areObjects && !deepEqual(val1, val2)) || (!areObjects && val1 !== val2)) {
      return false;
    }
  }
  return true;
}

function isObject(object: object) {
  return object != null && typeof object === 'object';
}

export function isErrorResponse(error: unknown): error is ErrorResponse {
  return (
    typeof error === 'object' &&
    error != null &&
    (typeof Reflect.get(error, 'error_type') === 'string' || typeof Reflect.get(error, 'error_code') === 'string')
  );
}

// related to product type
export function isCustomPackOrProduct(product: Product): product is CustomPackOrProduct {
  return product.type.id === ProductTypes.CUSTOM_PACK || product.type.id === ProductTypes.CUSTOM_PRODUCT;
}

export function isCustomPack(product: Product): product is CustomPack {
  return product.type.id === ProductTypes.CUSTOM_PACK;
}

export function isPackPart(part: PartLine): boolean {
  return (
    part.part_type.id === ProductTypes.CUSTOM_PACK ||
    part.part_type.id === ProductTypes.PACK_TEMPLATE ||
    part.part_type.id === ProductTypes.CUSTOM_PACK_TEMPLATE
  );
}

export function isPackProduct(product: Product): product is PackProduct {
  return (
    product.type.id === ProductTypes.CUSTOM_PACK ||
    product.type.id === ProductTypes.PACK_TEMPLATE ||
    product.type.id === ProductTypes.CUSTOM_PACK_TEMPLATE
  );
}

export function isPackTemplate(product: Product): product is PackTemplate {
  return product.type.id === ProductTypes.PACK_TEMPLATE;
}

export function isCustomPackTemplate(product: Product): product is CustomPackTemplate {
  return product.type.id === ProductTypes.CUSTOM_PACK_TEMPLATE;
}

export function isSupplierProduct(product: Product): product is Product {
  return product.type.id === ProductTypes.SUPPLIER_PRODUCT;
}

// related to cart item type
export function isCartItemProduct(cartItem: CartItem | null): cartItem is CartItemProduct & CartItemExtraFields {
  return cartItem?.type === CartItemType.product;
}

export function isCartItemPack(cartItem: CartItem | null): cartItem is CartItemPack & CartItemExtraFields {
  return cartItem?.type === CartItemType.pack;
}

// related to design status
export function isPendingDesign(status: DesignStatus | null) {
  return status?.id === DesignStatusId.PENDING_DESIGN;
}

export function isDesignReady(status: DesignStatus | null) {
  return status?.id === DesignStatusId.DESIGN_READY;
}

export function isChangesRequested(status: DesignStatus | null) {
  return status?.id === DesignStatusId.CHANGES_REQUESTED;
}

export function isApproved(status: DesignStatus | null) {
  return status?.id === DesignStatusId.APPROVED;
}

export function isPackOrProductPOD(product: Product) {
  return isPackProduct(product) ? product.items.every((i) => i.product?.is_pod) : product.is_pod;
}

export const findPartInCart = (
  cartItem: CartItem | undefined,
  attributes: ItemAttributes[] | AttributeValues[],
  configurationType: string
) => {
  if (!cartItem) return false;
  const cartItemAttribute = cartItem.items_attributes || {};
  if (Object.keys(cartItemAttribute).length === 0) return false;
  return deepEqual(cartItemAttribute, attributes) && cartItem.configuration_type === configurationType;
};

export const getCurrencyFormat = (price: number) => {
  return price.toLocaleString('us-US', {
    minimumFractionDigits: 2,
  });
};

export function getAttributes(attributes: AttributeValue[]) {
  const attrs: { [attribute: string]: string } = {};
  attributes.forEach((attr) => {
    attrs[attr.name] = attr.value;
  });
  return attrs;
}

export function getCartItemId(cartItem: CartItem) {
  const idPrefix = `${cartItem.product.id}>${cartItem.configuration_type}>`;

  return (
    idPrefix +
    (isCartItemPack(cartItem)
      ? cartItem.parts?.map((part) =>
          part.items.map((packItem) => {
            return `${packItem.product_id}-${GetCartItemPartAttributesId(packItem.attributes)}`;
          })
        )
      : cartItem.parts.map((part) => {
          return GetCartItemPartAttributesId(part.attributes);
        })
    )
      ?.sort()
      .join('/')
      .replaceAll(' ', '')
  );
}

export function getCartItemIdWithoutSize(cartItem: CartItem) {
  const idPrefix = `${cartItem.product.id}>${cartItem.configuration_type}>`;

  return (
    idPrefix +
    (isCartItemPack(cartItem)
      ? cartItem.parts?.map((packItem, idx) => {
          if (idx !== 0) return '';
          return packItem.items.map((item) => {
            return `${item.product_id}-${item.attributes
              ?.map((attr) => {
                if (attr.name === 'size') return '';
                return `${attr.name}:${attr.value}`;
              })
              .sort()
              .join()}`;
          });
        })
      : cartItem.parts.map((part, idx) => {
          if (idx !== 0) return '';
          return part.attributes
            ?.map((attr) => {
              if (attr.name === 'size') return '';
              return `${attr.name}:${attr.value}`;
            })
            .sort()
            .join();
        })
    )
      ?.sort()
      .join('')
      .replaceAll(' ', '')
  );
}

export function getCartItemPartId(cartItem: CartItem, part: CartItemPackPart | CartItemProductPart) {
  const idPrefix = `${cartItem.product.id}>${cartItem.configuration_type}>`;

  if (isCartItemPack(cartItem) && 'items' in part) {
    return idPrefix + getAttributesId(part.items);
  } else if ('attributes' in part) {
    return idPrefix + GetCartItemPartAttributesId(part.attributes)?.replaceAll(' ', '');
  }
}

// for CartItemProductPart this function can be used like this
// getAttributesId([{product_id: CartItem.product.id, attributes: CartItemProductPart.attributes}])
export function getAttributesId(attributes: ItemAttributes[]) {
  return attributes
    .map((packItem) => {
      return `${packItem.product_id}-${GetCartItemPartAttributesId(packItem.attributes)}`;
    })
    .sort()
    .join('/')
    .replaceAll(' ', '');
}

function GetCartItemPartAttributesId(attributes: AttributeValue[] | undefined) {
  return attributes
    ?.map((attr) => `${attr.name}:${attr.value}`)
    .sort()
    .join();
}

export function getFormattedPrice(number: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
}

export function findPaymentById(id: string | undefined, { card, us_bank_account }: PaymentMethodsResponse) {
  if (!id) {
    return undefined;
  }
  return card?.find((m) => m.id === id) || us_bank_account?.find((m) => m.id === id);
}

export function typedBoolean<T>(value: T): value is Exclude<T, '' | 0 | false | null | undefined> {
  return Boolean(value);
}

export function getBase64ImgFromHTMLElement(element: HTMLElement) {
  return (
    htmlToImage
      // Why cacheBust?
      // https://github.com/tsayen/dom-to-image/issues/390#issuecomment-935933918
      .toPng(element, { cacheBust: true })
      .catch((error) => {
        console.error('Something went wrong while generating the Image Texture!', error);
        return undefined;
      })
  );
}

export function isFetching(fetchers: Fetcher[], fetchersToCheck: string[]) {
  return fetchers.some((f) => fetchersToCheck.includes(f.submission?.formData.get('_action')?.toString() ?? ''));
}

export function getEmptyArray(length: number) {
  return Array(length)
    .fill(null)
    .map((_, i) => i + 1);
}

export function getCartPartsLength(cart: Cart) {
  return cart.products.reduce((total: number, product: CartItem) => total + product.parts.length, 0);
}

export function buildSrcSet(imageName: string) {
  const name = baseImgUrl + imageName;
  return {
    src: `${name}.png`,
    srcSet: `${name}.png, ${name}-2x.png 2x, ${name}-3x.png 3x`,
  };
}

export function days(number: number | string) {
  return `${number} business day${+number > 1 ? 's' : ''}`;
}

export const PAGE_SIZE = 24;

function getTagsParamValue(searchParams: URLSearchParams) {
  return searchParams.get(TAGS_PARAM) ?? '';
}

export function getApiQuery(searchParams: URLSearchParams, limit: number = PAGE_SIZE) {
  const search = getSearchParamValue(searchParams);
  const tagsSearch = getTagsParamValue(searchParams);

  const page = getPageParamValue(searchParams);
  const offset = ((page - 1) * limit).toString();

  return {
    offset,
    limit: limit.toString(),
    ordering: '-created_at',
    ...(search ? { [SEARCH_PARAM]: search } : {}),
    ...(tagsSearch ? { [TAGS_PARAM]: tagsSearch } : {}),
  };
}

export function getHrefWithLogoQuery(searchParams: URLSearchParams, href?: string) {
  const [pathname, query] = href?.split('?') as string[];
  const searchParamsWithLogo = new URLSearchParams(query);

  const logoParam = searchParams.get(LOGO_PARAM);
  const automockupsActiveParam = searchParams.get(AUTOMOCKUPS_PARAM);
  const domainParam = searchParams.get(DOMAIN_PARAM);

  if (logoParam) {
    searchParamsWithLogo.set(LOGO_PARAM, logoParam);
  }
  if (automockupsActiveParam) {
    searchParamsWithLogo.set(AUTOMOCKUPS_PARAM, automockupsActiveParam);
  }
  if (domainParam) {
    searchParamsWithLogo.set(DOMAIN_PARAM, domainParam);
  }

  const searchParamsString = searchParamsWithLogo.toString();

  const hrefWithQuery = searchParamsString.length > 0 ? pathname + '?' + searchParamsString : pathname;

  return hrefWithQuery;
}

export async function checkValidImageUrl(url: string) {
  try {
    new URL(url);
    const res = await fetch(url, { method: 'HEAD', mode: 'cors' });
    const buff = await res.blob();

    return buff.type.startsWith('image/');
  } catch (error) {
    return false;
  }
}

export function getPhysicalProperties(obj: fabric.Object, activeImprintArea: ImprintAreaWithImage) {
  const img = document.createElement('img');
  img.src = activeImprintArea.image.media;

  const { scaleRatio } = calculateCanvasRatio(img.width, img.height, activeImprintArea.imprint_area);

  const { left, right, upper, lower } = activeImprintArea.imprint_area;
  const inchesWidthRatio = activeImprintArea.physical_size.widthInches / Math.abs(right - left); // Inches per pixel
  const inchesHeightRatio = activeImprintArea.physical_size.heightInches / Math.abs(upper - lower); // Inches per pixel

  const currentWidth = obj.width * obj.scaleX;
  const currentHeight = obj.height * obj.scaleY;
  return {
    physical_size: {
      width: round(currentWidth * scaleRatio * inchesWidthRatio),
      height: round(currentHeight * scaleRatio * inchesHeightRatio),
    },
    physical_placement: {
      top_left_x: round(obj.left * scaleRatio * inchesWidthRatio),
      top_left_y: round(obj.top * scaleRatio * inchesHeightRatio),
    },
  };
}

export function round(num: number, precision = 2) {
  return parseFloat(num.toFixed(precision));
}

export function calculateCanvasRatio(
  originalImageWidth: number,
  originalImageHeight: number,
  imprintArea: ImprintAreaWithImage['imprint_area']
) {
  const scaleFactor = Math.min(dimensions.width / originalImageWidth, dimensions.height / originalImageHeight);
  const scaledWidth = originalImageWidth * scaleFactor;
  const scaledHeight = originalImageHeight * scaleFactor;

  const scaleRatio = originalImageWidth / scaledWidth;

  const diff = scaledWidth <= scaledHeight ? dimensions.width - scaledWidth : dimensions.height - scaledHeight;
  const offset = round(diff / 2);

  const scaledCanvasPosition = {
    top: round(imprintArea.upper / scaleRatio),
    bottom: round(imprintArea.lower / scaleRatio),
    left: round(imprintArea.left / scaleRatio),
    right: round(imprintArea.right / scaleRatio),
  };

  return { scaleRatio, offset, scaledCanvasPosition, scaledWidth, scaledHeight };
}
